/*
 * GEHC CONFIDENTIAL
 *
 * Copyright (c) 2025, 2025, GE HealthCare
 * All Rights Reserved
 *
 * This unpublished material is proprietary to GE HealthCare. The methods and
 * techniques described herein are considered trade secrets and/or
 * confidential. Reproduction or distribution, in whole or in part, is
 * forbidden except by express written permission of GE HealthCare.
 * GE is a trademark of General Electric Company used under trademark license.
 */

import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { PostMessengerService } from '@app/di/channel/window/post-messenger.service';
import { Logger } from '@core/lib/services';
import { MicroAppStatus } from '../window/postmessage.types';

const log = new Logger('MicroAppBase');

/**
 * Abstract base class for micro applications
 * Contains common functionality shared between Modal and Window micro apps
 * @template T The type of the status data from PostMessage
 */
export abstract class MicroAppBase<T = any> {
  protected _status: BehaviorSubject<T>;
  protected _minimized = false;
  protected destroy$ = new Subject<void>();
  protected _appReady = false;
  constructor(
    public readonly microAppType: 'Modal' | 'Window',
    public readonly appID: string,
    public readonly url: URL,
    public readonly sessionID: string,
    protected postMessageService: PostMessengerService
  ) {
    const modelInitializeStatus = {
      appID: this.appID,
      type: MicroAppStatus.NOT_STARTED,
      sessionID: this.sessionID
    } as any;
    this._status = new BehaviorSubject<T>(microAppType === 'Modal' ? modelInitializeStatus : null);
  }

  get appReady(): boolean {
    return this._appReady;
  }

  set appReady(value: boolean) {
    this._appReady = value;
  }

  get status(): Observable<T> {
    return this._status.asObservable();
  }

  get minimized(): boolean {
    return this._minimized;
  }

  get statusValue(): T {
    return this._status.getValue();
  }

  abstract launch(): Promise<void>;
  abstract restore(): void;
  abstract minimize(): void;
  abstract close(): void;
  abstract isActive(): boolean;
  abstract updateStatus(status: T): void;

  protected cleanup(): void {
    log.debug(`Cleaning up micro app: ${this.appID}`);
    this._status.complete();
    this.destroy$.complete();
    this.postMessageService.unregister(this.appID);
  }

  waitForAppReady(timeout: number): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      const checkInterval = 1000;
      let elapsedTime = 0;

      const intervalId = setInterval(() => {
        if (this.appReady) {
          clearInterval(intervalId);
          resolve(true);
        } else if (elapsedTime >= timeout) {
          log.error('Timeout waiting for app: ' + this.appID + ' to be ready');
          clearInterval(intervalId);
          reject(false);
        }
        elapsedTime += checkInterval;
      }, checkInterval);
    });
  }

  sendMessage(msg: any): void {
    this.postMessageService.sendMessage(this.appID, msg);
  }
}
